/**
 * Flow-field pathfinding.
 *
 * A Dijkstra sweep from the goal tiles produces an integrated cost field; units
 * then just walk downhill. One field serves any number of units heading for the
 * same place, which is what makes 400-unit waves affordable — and it is the
 * only thing that makes a horde of tens of thousands affordable at all.
 *
 * Walls are *passable at a price*: their traversal cost scales with remaining
 * health. That single choice gives us the behaviour the brief asks for — the
 * horde flows around a strong wall, piles onto a weak one, and pours through a
 * breach the instant it opens, because the breach genuinely becomes the cheapest
 * route rather than because anything special-cased it.
 *
 * ## The sweep is resumable, and that is the whole of the scaling story
 *
 * One sweep is O(tiles log tiles), and on a 384×384 map that is about two
 * hundred milliseconds — six dropped frames, every time a building falls, which
 * during a siege is constantly. The average tick looked fine and the game
 * stuttered anyway.
 *
 * So a rebuild is *sliced*. `begin` starts one into a shadow buffer, `advance`
 * expands a bounded number of tiles, and the new field is published — swapped
 * in whole — only when it is finished. Nothing ever reads a half-built field,
 * so a unit's routing is always internally consistent; it is simply the routing
 * of a world as it stood a fraction of a second ago, which is the correct thing
 * for a horde that has not yet noticed the breach.
 *
 * The slicing is driven by the tick counter and a fixed budget, never by a
 * clock, so two runs of the same seed publish on exactly the same tick. That is
 * the difference between this and the per-tick *time* budget that broke
 * save/load in the path cache — see the note in `path.ts`.
 */

import { MAP_H, MAP_TILES, MAP_W } from './constants.ts';
import { alliedWith, OB_SOLID, TERRAIN_MOVE_COST, WallKind } from './types.ts';
import type { TileMap } from './map.ts';

const UNREACHED = 1e9;

/** 8-way neighbour offsets; diagonals cost sqrt(2). */
const NX = [1, -1, 0, 0, 1, 1, -1, -1];
const NY = [0, 0, 1, -1, 1, -1, 1, -1];
const NCOST = [1, 1, 1, 1, 1.41421356, 1.41421356, 1.41421356, 1.41421356];

/** Minimum extra cost of chewing through a structure, in tile-steps. */
const WALL_BASE_COST = 14;
const WALL_HP_COST = 0.055;
const BUILDING_BASE_COST = 22;

export class FlowField {
  /**
   * The published field. Not `readonly`: publishing swaps the buffers, which
   * is what makes a sliced rebuild invisible to every reader.
   */
  dist = new Float32Array(MAP_TILES);
  dirX = new Float32Array(MAP_TILES);
  dirY = new Float32Array(MAP_TILES);
  /** Tick the field was last published on. */
  stamp = -1;
  /** Goal centre, for fallback steering when a tile is unreachable. */
  goalX = 0;
  goalY = 0;

  /** The field under construction. Swapped with the published one on finish. */
  private sDist = new Float32Array(MAP_TILES);
  private sDirX = new Float32Array(MAP_TILES);
  private sDirY = new Float32Array(MAP_TILES);
  private sGoalX = 0;
  private sGoalY = 0;

  /**
   * The frontier heap, and the `closed` set that keeps it honest.
   *
   * This is a lazy-deletion Dijkstra: there is no decrease-key, so a tile is
   * pushed again every time a cheaper route to it is found, and the heap can
   * therefore hold *more entries than there are tiles*. It was sized at exactly
   * one entry per tile, which is a bound that does not hold — and a typed array
   * silently drops a write past its end, so an overflow does not throw, it
   * quietly corrupts the frontier. The result was a sweep that never converged
   * properly and kept re-pushing: on a flat battlefield, fifteen million pushes
   * for a hundred and fifty thousand tiles, and three seconds where it should
   * have been fifty milliseconds.
   *
   * Two fixes, both needed. The heap grows rather than overflowing, and a tile
   * is expanded once — the first time it is popped is the time its distance is
   * final, and every later entry for it is stale by construction.
   */
  private heapIdx = new Int32Array(MAP_TILES + 1);
  private heapKey = new Float32Array(MAP_TILES + 1);
  private heapSize = 0;
  private readonly closed = new Uint8Array(MAP_TILES);

  /** Sweep parameters, held across the ticks a sliced rebuild spans. */
  private map: TileMap | null = null;
  private attackerTeam = 0;
  private buildingTeam: ((b: number) => number) | null = null;
  private smashWalls = true;
  private active = false;
  /**
   * Battlefield scatter, or null.
   *
   * A field is one byte a tile and this loop touches eight neighbours of every
   * tile on the map, so it is held as a local in `advance` rather than looked up
   * through the World — which this class deliberately does not know about. Null
   * in every settlement, which is where the Dijkstra actually runs hot.
   */
  private obstacles: Uint8Array | null = null;

  /** True while a rebuild is part-done and has not been published. */
  get rebuilding(): boolean {
    return this.active;
  }

  /**
   * A binary heap with hole-based sifting: the moving element is held in a
   * local rather than written into every position it passes through, which is
   * roughly half the memory traffic of the swap form. This is the inner loop of
   * the most expensive thing the simulation does.
   */
  private push(idx: number, key: number): void {
    if (this.heapSize + 1 >= this.heapIdx.length) this.growHeap();
    let i = ++this.heapSize;
    const hi = this.heapIdx;
    const hk = this.heapKey;
    while (i > 1) {
      const p = i >> 1;
      if (hk[p] <= key) break;
      hi[i] = hi[p];
      hk[i] = hk[p];
      i = p;
    }
    hi[i] = idx;
    hk[i] = key;
  }

  private growHeap(): void {
    const idx = new Int32Array(this.heapIdx.length * 2);
    const key = new Float32Array(this.heapKey.length * 2);
    idx.set(this.heapIdx);
    key.set(this.heapKey);
    this.heapIdx = idx;
    this.heapKey = key;
  }

  private pop(): number {
    if (this.heapSize === 0) return -1;
    const hi = this.heapIdx;
    const hk = this.heapKey;
    const top = hi[1];
    const lastIdx = hi[this.heapSize];
    const lastKey = hk[this.heapSize];
    this.heapSize--;
    if (this.heapSize > 0) {
      let i = 1;
      for (;;) {
        let m = i << 1;
        if (m > this.heapSize) break;
        if (m + 1 <= this.heapSize && hk[m + 1] < hk[m]) m++;
        if (hk[m] >= lastKey) break;
        hi[i] = hi[m];
        hk[i] = hk[m];
        i = m;
      }
      hi[i] = lastIdx;
      hk[i] = lastKey;
    }
    return top;
  }

  /**
   * Rebuild the field in one go. `goals` is a flat list of tile indices.
   * `attackerTeam` decides whose structures count as obstacles: a besieger will
   * smash through the defender's walls, but never through its own siege lines.
   */
  compute(
    map: TileMap,
    goals: number[],
    attackerTeam: number,
    buildingTeam: (b: number) => number,
    smashWalls = true,
    obstacles: Uint8Array | null = null,
  ): void {
    this.begin(map, goals, attackerTeam, buildingTeam, smashWalls, obstacles);
    this.advance(Number.POSITIVE_INFINITY);
  }

  /** Start a rebuild. Nothing published yet; the live field is untouched. */
  begin(
    map: TileMap,
    goals: number[],
    attackerTeam: number,
    buildingTeam: (b: number) => number,
    smashWalls = true,
    obstacles: Uint8Array | null = null,
  ): void {
    this.map = map;
    this.attackerTeam = attackerTeam;
    this.buildingTeam = buildingTeam;
    this.smashWalls = smashWalls;
    this.obstacles = obstacles;
    this.heapSize = 0;
    this.active = true;

    this.sDist.fill(UNREACHED);
    this.closed.fill(0);
    // `dirX`/`dirY` are deliberately *not* cleared: `sample` reads them only
    // where `dist` is finite, and every such tile has them written during
    // relaxation. The exceptions are the goal tiles themselves, which are
    // seeded rather than relaxed into, so they are zeroed explicitly. That is
    // two writes instead of three hundred thousand.
    if (goals.length === 0) {
      this.active = false;
      this.publish();
      return;
    }
    let gx = 0;
    let gy = 0;
    for (const g of goals) {
      this.sDist[g] = 0;
      this.sDirX[g] = 0;
      this.sDirY[g] = 0;
      this.push(g, 0);
      gx += g % MAP_W;
      gy += (g / MAP_W) | 0;
    }
    this.sGoalX = gx / goals.length + 0.5;
    this.sGoalY = gy / goals.length + 0.5;
  }

  /**
   * Expand at most `budget` tiles. Returns true when the sweep finished and the
   * new field was published.
   *
   * Flow directions are recorded during relaxation rather than in a second
   * sweep. When we relax `cur → ni`, the cheapest way *out* of `ni` is back to
   * `cur`, so the flow vector is known right here. Folding it in removes a full
   * 8-neighbour pass over every tile on the map, which on a large grid was, by
   * a wide margin, the most expensive thing in the whole simulation.
   */
  advance(budget: number): boolean {
    if (!this.active) return false;
    const map = this.map as TileMap;
    const buildingTeam = this.buildingTeam as (b: number) => number;
    const smashWalls = this.smashWalls;
    const attackerTeam = this.attackerTeam;
    const dist = this.sDist;
    const dirX = this.sDirX;
    const dirY = this.sDirY;

    const terrain = map.terrain;
    const wall = map.wall;
    const wallHp = map.wallHp;
    const building = map.building;
    const obstacles = this.obstacles;
    const minerals = map.resourceSolid;

    const closed = this.closed;
    let done = 0;
    while (this.heapSize > 0 && done < budget) {
      const cur = this.pop();
      // Every entry after the first for a given tile is stale: distances come
      // off the heap in nondecreasing order, so the first pop is the final one.
      if (closed[cur] === 1) continue;
      closed[cur] = 1;
      done++;
      const cd = dist[cur];
      const cx = cur % MAP_W;
      const cy = (cur / MAP_W) | 0;

      for (let n = 0; n < 8; n++) {
        const nx = cx + NX[n];
        const ny = cy + NY[n];
        if (nx < 0 || ny < 0 || nx >= MAP_W || ny >= MAP_H) continue;
        const ni = ny * MAP_W + nx;

        // A boulder is not passable at a price. Unlike a wall, there is nothing
        // to break through and no gate in it — the route goes round or it does
        // not exist, which is what makes a rock an anchor for a flank.
        if (obstacles !== null && (obstacles[ni] & OB_SOLID) !== 0) continue;
        if (minerals?.[ni]) continue;
        if (n >= 4) {
          const sideX = cy * MAP_W + nx, sideY = ny * MAP_W + cx;
          if (minerals?.[sideX] || minerals?.[sideY]
            || (obstacles !== null && ((obstacles[sideX] | obstacles[sideY]) & OB_SOLID))) continue;
          // A siege route may pay to enter a wall tile, never slip diagonally
          // between intact wall corners without first breaking either one.
          if ((wall[sideX] !== WallKind.None && (smashWalls || wall[sideX] !== WallKind.Gate))
            || (wall[sideY] !== WallKind.None && (smashWalls || wall[sideY] !== WallKind.Gate))) continue;
          if (building[sideX] >= 0 || building[sideY] >= 0) continue;
        }

        // Cost of *entering* the neighbour, i.e. of a unit standing there.
        let step = NCOST[n] * (TERRAIN_MOVE_COST[terrain[ni]] ?? 1);

        if (wall[ni] !== WallKind.None) {
          if (!smashWalls) {
            // Defenders route around their own stonework and through their gates.
            if (wall[ni] !== WallKind.Gate) continue;
            step += 0.35;
          } else {
            step += WALL_BASE_COST + wallHp[ni] * WALL_HP_COST;
          }
        } else {
          const bIdx = building[ni];
          if (bIdx >= 0) {
            if (alliedWith(buildingTeam(bIdx), attackerTeam)) {
              // Never route through your own side's buildings. Alliance rather
              // than equality: a second banner on your side owns the same
              // roofs you do, and identical for the two teams that existed
              // before there were four.
              continue;
            }
            step += BUILDING_BASE_COST;
          }
        }

        const nd = cd + step;
        if (nd < dist[ni]) {
          dist[ni] = nd;
          const inv = 1 / NCOST[n];
          dirX[ni] = -NX[n] * inv;
          dirY[ni] = -NY[n] * inv;
          this.push(ni, nd);
        }
      }
    }

    if (this.heapSize > 0) return false;
    this.active = false;
    this.publish();
    return true;
  }

  /** Swap the finished shadow field in. */
  private publish(): void {
    const d = this.dist;
    const x = this.dirX;
    const y = this.dirY;
    this.dist = this.sDist;
    this.dirX = this.sDirX;
    this.dirY = this.sDirY;
    this.sDist = d;
    this.sDirX = x;
    this.sDirY = y;
    this.goalX = this.sGoalX;
    this.goalY = this.sGoalY;
  }

  /** Sample the field at a world position, with bilinear-ish smoothing. */
  sample(x: number, y: number, out: { x: number; y: number }): boolean {
    const tx = Math.floor(x);
    const ty = Math.floor(y);
    if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) {
      out.x = 0;
      out.y = 0;
      return false;
    }
    const i = ty * MAP_W + tx;
    if (this.dist[i] >= UNREACHED) {
      // Unreachable: fall back to steering straight at the goal.
      const dx = this.goalX - x;
      const dy = this.goalY - y;
      const l = Math.hypot(dx, dy) || 1;
      out.x = dx / l;
      out.y = dy / l;
      return false;
    }
    out.x = this.dirX[i];
    out.y = this.dirY[i];
    return true;
  }

  tileAt(x: number, y: number): number {
    const tx = Math.floor(x);
    const ty = Math.floor(y);
    if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return -1;
    return ty * MAP_W + tx;
  }
}
